import { DateInput } from "@msuite/ui/date-input";
import { Divider } from "@msuite/ui/divider";
import { FileUpload, type FileUploadController } from "@msuite/ui/file-upload";
import { FormCheckbox, Textarea } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import type { ProjectTimelineNoteSchema } from "@msuite/utils/schemas";
import React from "react";
import { useFormContext } from "react-hook-form";
import { FileCardRemote } from "@/components/files";
import type { ExistingFileController } from "@/hooks/use-existing-file-controller";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { ProjectTimelineNoteFormSelectExistingFiles } from "./project-timeline-note-form-select-existing-files";

/** Props Interface */
interface ProjectTimelineNoteFormProps {
	controller: FileUploadController | null;
	projectId?: string;
	existingFileController?: ExistingFileController;
}

export function ProjectTimelineNoteForm({
	controller,
	projectId,
	existingFileController,
}: ProjectTimelineNoteFormProps) {
	/** Context */
	const methods = useFormContext<ProjectTimelineNoteSchema>();

	/** Hooks */
	const { t } = useTranslation();
	const mentionsOptions = useMentionOptions();

	/** Render */
	return (
		<div className="v-stack gap-4">
			<DateInput
				control={methods.control}
				name="date"
				label={t("dates.date")}
				isRequired
				data-cy="project-timeline-note-date-input"
			/>
			<Textarea
				isRequired
				label={t("notes.note")}
				name="note"
				control={methods.control}
				{...mentionsOptions}
				data-cy="project-timeline-note-textarea"
			/>
			<FormCheckbox
				control={methods.control}
				name="isImportant"
				data-cy="project-timeline-note-important-checkbox"
			>
				{t("projects.include_note_in_diary")}
			</FormCheckbox>
			{!controller ? null : (
				<React.Fragment>
					<Divider variant="modal" />
					<FileUpload
						withLabel={false}
						controller={controller}
						renderFile={(file) => (
							<FileCardRemote
								fileId={file.id}
								onInteraction={async () => {}}
							/>
						)}
					>
						{projectId && existingFileController && (
							<ProjectTimelineNoteFormSelectExistingFiles
								controller={controller}
								projectId={projectId}
								existingFileController={existingFileController}
							/>
						)}
					</FileUpload>
				</React.Fragment>
			)}
		</div>
	);
}
